import { FunctionComponent, useEffect, useState } from 'react';
import {
    Card,
    CardContent,
    Container,
    Divider,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CardListRow } from '@/components/Card/CardListRow';
import { useAuthentication } from '@/hooks/useAuthentication';
import {
    useGetProductPersonNameConfigurationQuery,
    useGetTextReplacementQuery,
    usePostTextReplacementsMutation,
} from '@/api/v2/textReplacement';
import {
    ConfigurationIdNameMap,
    convertDetailToProductTextReplacement,
    ProductTextReplacement,
    ProductTextReplacementDetail,
} from '@/models/textReplacement';
import { MUTATION_CACHE_KEY } from '@/pages/textCustomizations/components/TextReplacementContent';
import { AddProductPersonName } from '@/pages/textCustomizations/components/AddProductPersonName';
import { ChangeProductPersonName } from '@/pages/textCustomizations/components/ChangeProductPersonName';

type ProductCombinationCardProps = {
    onChangeProductPersonNames: (
        productTextReplacement: ProductTextReplacement[]
    ) => void;
    onSave: () => void;
};

export const ProductCombinationCard: FunctionComponent<
    ProductCombinationCardProps
> = ({ onChangeProductPersonNames, onSave }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { data: productNameConfig } =
        useGetProductPersonNameConfigurationQuery(liftOperatorId);
    const { data: textReplacement } = useGetTextReplacementQuery({
        liftOperatorId,
    });
    const [, { isLoading }] = usePostTextReplacementsMutation({
        fixedCacheKey: MUTATION_CACHE_KEY,
    });
    const [productPersonEntities, setProductPersonEntities] = useState<
        ProductTextReplacementDetail[]
    >([]);

    useEffect(() => {
        if (!!productNameConfig && !!textReplacement) {
            const { productTextReplacements } = textReplacement;

            const filledReplacements = productTextReplacements.map(
                (replacement) => {
                    const person: ConfigurationIdNameMap =
                        productNameConfig.persons.find(
                            (p) => p.id === replacement.personTypeId
                        );
                    const product: ConfigurationIdNameMap =
                        productNameConfig.products.find(
                            (p) => p.id === replacement.productId
                        );

                    return {
                        ...replacement,
                        productName: person.name,
                        personTypeName: product.name,
                    };
                }
            );

            setProductPersonEntities(filledReplacements);
        }
    }, [textReplacement, productNameConfig]);

    return (
        <Container
            sx={{
                ml: 'unset',
            }}
            maxWidth={false}
        >
            <Card
                sx={{
                    width: '100%',
                }}
            >
                <CardContent
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant='h6'>
                        Texte für definierte Ticket-Personen-Kombinationen
                        anpassen
                    </Typography>

                    <LoadingButton
                        sx={{
                            ml: 'auto',
                        }}
                        variant='contained'
                        loading={isLoading}
                        onClick={onSave}
                    >
                        Speichern
                    </LoadingButton>
                </CardContent>
                <Divider
                    sx={{
                        width: '100%',
                    }}
                />

                {!!productNameConfig && (
                    <CardListRow>
                        <AddProductPersonName
                            persons={productNameConfig?.persons}
                            products={productNameConfig?.products}
                            onAddLine={({ product, person }) => {
                                const newEmptyMultiString = {
                                    de: '',
                                    en: '',
                                };
                                setProductPersonEntities((prevState) => [
                                    {
                                        productId: product.id,
                                        personTypeId: person.id,
                                        personTypeName: person.name,
                                        productName: product.name,
                                        newProductName: newEmptyMultiString,
                                        newPersonTypeName: newEmptyMultiString,
                                    },
                                    ...prevState,
                                ]);
                            }}
                        />
                    </CardListRow>
                )}

                {productPersonEntities.length > 0 && (
                    <Divider
                        sx={{
                            my: 6,
                        }}
                    />
                )}

                <CardListRow>
                    <ChangeProductPersonName
                        productTextReplacements={productPersonEntities}
                        onChange={(productTextReplacements) => {
                            const cleaned = productTextReplacements.map(
                                convertDetailToProductTextReplacement
                            );

                            setProductPersonEntities(productTextReplacements);

                            onChangeProductPersonNames(cleaned);
                        }}
                    />
                </CardListRow>
            </Card>
        </Container>
    );
};
